import React, { useState } from "react";
import {
  useTheme,
  Grid,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { IOrder } from "@/interfaces";
import { enqueueSnackbar } from "notistack";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import { UserStore } from "@/store/user";
import { useNavigate } from "react-router-dom";
import { OrderStore } from "@/store/orders";
import { ORDER_STATUS } from "@/@types/EnumType";
import MarketContractStore from "@/store/contract";
import Review from "../Modal/Review";
import { formatedBigNumber } from "@/utility";
import CommonStore, { UI_ACTION_STATE } from "@/store/common";
import { CanOrderBeCancel, CanUserCancelOrder } from "@/utility/order";

interface IManageOrder {
  selectedOrder: IOrder;
  setSelectedOrder: (newValue: IOrder | null) => void;
  userStore?: UserStore;
  orderStore?: OrderStore;
  contractStore?: MarketContractStore;
  commonStore?: CommonStore;
}
const ManageOrder = inject(
  Store.type.userStore,
  Store.type.orderStore,
  Store.type.contractStore,
  Store.type.commonStore,
)(
  observer(
    ({
      setSelectedOrder,
      selectedOrder,
      userStore,
      orderStore,
      contractStore,
      commonStore,
    }: IManageOrder) => {
      const theme = useTheme();
      const navigate = useNavigate();

      const [openDialog, setOpenDialog] = useState(false);
      const [isSubmiting, setIsSubmiting] = useState(false);

      const handleCancelOrder = async () => {
        setIsSubmiting(true);

        if (!CanOrderBeCancel(selectedOrder)) {
          enqueueSnackbar(
            "Cancel order failed: Order can only be cancelled after 1 hour of creation.",
            {
              variant: "error",
            },
          );
          setIsSubmiting(false);
          return;
        }

        if (orderStore && contractStore && userStore?.userId) {
          try {
            if (selectedOrder?.contract_order_id) {
              commonStore?.setActionState(
                UI_ACTION_STATE.WALLET_CONFIRMATION_PENDING,
              );
              await contractStore.cancelOrder(
                selectedOrder.contract_order_id,
                (result) => {
                  commonStore?.setActionState(result);
                },
              );
            } else {
              throw new Error("Couldn't find contract order id");
            }
            await orderStore.updateOrderStatus({
              order_id: selectedOrder.order_id,
              status: ORDER_STATUS.CANCELLED,
              user_id: userStore.userId,
            });
            setSelectedOrder(null);
            enqueueSnackbar("Order removed successfully.", {
              variant: "success",
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            console.error("CancelOrder error:", error);
            enqueueSnackbar("Cancel order failed: " + error.message, {
              variant: "error",
            });
            setIsSubmiting(false);
          } finally {
            commonStore?.setActionState(UI_ACTION_STATE.NONE);
          }
        }
      };

      return (
        <>
          <Dialog
            open={true}
            sx={{
              "& .MuiPaper-root": {
                px: "0rem",
                pt: "37.87px",
                pb: "56px",
                overflow: "hidden",
                width: "90%",
                maxWidth: "853px",
              },
            }}
          >
            <DialogTitle variant="h3" mb="31.13px" p="0 !important">
              Manage order
            </DialogTitle>
            <DialogContent
              sx={{
                px: "3rem !important",
                py: "0 !important",
              }}
            >
              <Grid container columnSpacing="40px" rowSpacing="29px">
                <Grid item sm={6}>
                  <br />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Store title"
                    type="text"
                    name="title"
                    id="title"
                    value={selectedOrder.post.title}
                    sx={{ mb: "29px" }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Order status"
                    type="text"
                    name="orderStatus"
                    id="orderStatus"
                    value={selectedOrder.status}
                    sx={{
                      mb: "29px",
                      "& .MuiInputBase-input": {
                        color: `${
                          selectedOrder.status === ORDER_STATUS.CANCELLED
                            ? theme.colors.red
                            : selectedOrder.status === ORDER_STATUS.CLOSED
                            ? theme.colors.darkFontColor
                            : theme.colors.green
                        } !important`,
                        WebkitTextFillColor: "initial !important",
                      },
                    }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Order type"
                    type="text"
                    name="orderType"
                    id="orderType"
                    value={selectedOrder.order_type}
                    sx={{
                      mb: "29px",
                      "& .MuiInputBase-input": {
                        color: `${
                          selectedOrder.order_type === "SELL"
                            ? theme.colors.red
                            : theme.colors.green
                        } !important`,
                        WebkitTextFillColor: "initial !important",
                      },
                    }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Asset"
                    type="text"
                    name="asset"
                    id="asset"
                    value={selectedOrder.asset.name}
                    sx={{ mb: "29px" }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Quantity"
                    type="text"
                    name="quantity"
                    id="quantity"
                    value={`${formatedBigNumber(selectedOrder.order_amount)} ${
                      selectedOrder.asset.symbol
                    }`}
                  />
                </Grid>
                <Grid
                  item
                  sm={6}
                  sx={{
                    paddingTop: "1.5rem",
                  }}
                >
                  <br />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Currency"
                    type="text"
                    name="currency"
                    id="currency"
                    value={selectedOrder.currency}
                    sx={{ mb: "29px" }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Price / Unit"
                    type="text"
                    name="price"
                    id="price"
                    value={`${formatedBigNumber(selectedOrder.price)} ${
                      selectedOrder.currency
                    }`}
                    sx={{ mb: "29px" }}
                  />
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Payment method"
                    type="text"
                    name="paymentMethod"
                    id="paymentMethod"
                    value={selectedOrder.payment.payment_method.payment_name}
                    sx={{ mb: "29px" }}
                  ></TextField>
                  <TextField
                    disabled
                    variant="outlined"
                    fullWidth
                    label="Term"
                    type="text"
                    name="description"
                    id="description"
                    value={selectedOrder.post.description}
                    multiline={true}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ mt: "42px" }}>
              <Stack
                direction="row"
                justifyContent="center"
                gap="20px"
                width="fit-content"
                mx="auto"
                height="fit-content"
                flexWrap="wrap"
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() =>
                    navigate(`/trade/orders/${selectedOrder.order_id}`)
                  }
                  loading={isSubmiting}
                  sx={{
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  Go to chat
                </Button>
                {CanUserCancelOrder(selectedOrder, userStore?.userId) && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={() => handleCancelOrder()}
                    loading={isSubmiting}
                  >
                    Cancel order
                  </Button>
                )}

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setSelectedOrder(null)}
                  loading={isSubmiting}
                  sx={{
                    color: theme.colors.gray2,
                    borderColor: theme.colors.gray2,
                    ":hover": {
                      borderColor: theme.colors.gray2,
                    },
                  }}
                >
                  Close
                </Button>
                <p>(You can only cancel order after 1 hour of creation)</p>
              </Stack>
            </DialogActions>
          </Dialog>
          {openDialog && (
            <Review
              openDialog={openDialog}
              setOpenDialog={setOpenDialog}
              selectedOrder={selectedOrder}
            />
          )}
        </>
      );
    },
  ),
);

export default ManageOrder;
